"use client";
import styles from "./HeroCarouselBlock.module.scss";

import { HighlightText } from "@/sanity/types";
import Squiggle from "@/images/squiggle.svg";
import type {
	AddressSearchSupportContent,
	CtaQueryResult,
	ImageQueryResult,
	NmiSearchModalContent,
} from "@/sanity/queries";
import clsx from "clsx";
import { ProductLookup } from "@/components/shared/ProductLookup/ProductLookup";
import { Swiper, SwiperClass, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Autoplay, Navigation, Pagination } from "swiper/modules";
import { PortableTextRichText } from "@/components/shared/PortableTextRichText/PortableTextRichText";
import { Picture } from "@/components/shared/Picture/Picture";
import { BREAKPOINTS } from "@/utilities/breakpoints";
import { Button, ButtonLink } from "@/components/shared/Button/Button";
import ArrowIcon from "@/images/icons/arrow-button.svg";
import { useEffect, useState } from "react";
import { kebabCase } from "@/utilities/string-helpers";

interface HeroCarouselBlockProps {
	className?: string;
	slides: Array<{
		title?: HighlightText | null;
		description?: string | null;
		image?: ImageQueryResult | null;
		cta?: CtaQueryResult | null;
	}>;
	isHomepage?: boolean;
	enableAutoScroll?: boolean | null;
	showAddressLookup?: boolean | null;
	customId?: string | null;
	searchParams?: Record<string, string>;
	addressSearchSupportContent?: AddressSearchSupportContent | null;
	nmiSearchModalContent?: NmiSearchModalContent | null;
}

export const HeroCarouselBlock = ({
	className,
	slides,
	isHomepage,
	enableAutoScroll,
	showAddressLookup,
	customId,
	searchParams,
	addressSearchSupportContent,
	nmiSearchModalContent,
}: HeroCarouselBlockProps) => {
	return (
		<section
			id={customId ? kebabCase(customId) : undefined}
			className={clsx(
				styles["hero-carousel"],
				showAddressLookup && styles["hero-carousel--address-lookup"],
				isHomepage && styles["hero-carousel--homepage"],
				slides.length === 1 && styles["hero-carousel--single"],
				className,
			)}
		>
			<div className={styles["hero-carousel__inner"]}>
				{isHomepage && (
					<div className={styles["hero-carousel__squiggle"]}>
						<Squiggle />
					</div>
				)}

				<Swiper
					className={styles["hero-carousel__swiper"]}
					modules={[Navigation, A11y, Pagination, Autoplay]}
					spaceBetween={0}
					autoplay={
						enableAutoScroll
							? { delay: 5000, disableOnInteraction: true, pauseOnMouseEnter: true }
							: false
					}
					breakpoints={{
						[BREAKPOINTS.lg]: {
							spaceBetween: 20,
						},
						[BREAKPOINTS.xl]: {
							spaceBetween: 40,
						},
					}}
					speed={666}
					slidesPerView={1}
					a11y={{ enabled: true }}
					pagination={{
						el: "." + styles["controls__pagination"],

						clickable: true,
					}}
					navigation={{
						nextEl: "." + styles["controls__navigation-button--next"],
						prevEl: "." + styles["controls__navigation-button--prev"],
					}}
					loop={false}
				>
					{slides.map((slide, index) => {
						return (
							<SwiperSlide key={index}>
								<div className={styles["slide"]}>
									<div className={styles["slide__squiggle"]}>
										<Squiggle />
									</div>
									<div className={styles["slide__content"]}>
										<h2 className={clsx(styles["slide__heading"])}>
											{slide.title && (
												<PortableTextRichText
													asFragment
													value={slide.title}
												/>
											)}
										</h2>

										{slide.description && (
											<div className={styles["slide__description"]}>{slide.description}</div>
										)}

										{(slide.cta?.showCta ?? true) && slide.cta?.link && (
											<ButtonLink
												showArrow
												href={slide.cta.link}
												label={slide.cta.label}
												target={slide.cta.openInNewTab ? "_blank" : undefined}
											/>
										)}
									</div>
									{slide.image?.image && (
										<Picture
											className={styles["slide__image"]}
											sanityImage={slide.image.image}
											alt={slide.image.altText}
											width={460}
											height={254}
											preload
											sizes={{
												[BREAKPOINTS.md]: { width: 992, height: 548 },
												[BREAKPOINTS.lg]: { width: 600, height: 728 },
												[BREAKPOINTS.xl]: { width: 960, height: 728 },
											}}
										/>
									)}
								</div>
							</SwiperSlide>
						);
					})}

					<SwiperControls />
				</Swiper>
			</div>

			{showAddressLookup && (
				<ProductLookup
					className={styles["hero-carousel__product-lookup"]}
					searchParams={searchParams}
					addressSearchSupportContent={addressSearchSupportContent}
					nmiSearchModalContent={nmiSearchModalContent}
				/>
			)}
		</section>
	);
};

const SwiperControls = () => {
	return (
		<div className={styles["controls"]}>
			<div className={styles["controls__pagination"]}></div>
			<div className={styles["controls__navigation"]}>
				<Button
					className={clsx(
						styles["controls__navigation-button"],
						styles["controls__navigation-button--prev"],
					)}
					label={<ArrowIcon />}
				/>
				<Button
					className={clsx(
						styles["controls__navigation-button"],
						styles["controls__navigation-button--next"],
					)}
					label={<ArrowIcon />}
				/>
			</div>
		</div>
	);
};
